// src/components/sections/FeaturesSection.tsx
import React from 'react';
import { ExternalLink, Edit2, EyeOff, Settings, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AgentData_V10, AgentHubPageViewMode, FeatureItemV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import { VisualElement } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

interface FeaturesSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

const FeatureCard: React.FC<{ feature: FeatureItemV10; isOwnerView: boolean; onAction: FeaturesSectionProps['onAction'] }> = ({ feature, isOwnerView, onAction }) => {
  const { t } = useTranslation('agent-common');
  const handleClick = () => { if (feature.detailsUrl && !isOwnerView) { onAction('VIEW_FEATURE_DETAILS', { url: feature.detailsUrl, featureId: feature.id }); } else if (isOwnerView) { onAction('MANAGE_FEATURE_ITEM', { featureId: feature.id }); } };
  const canClick = (feature.detailsUrl && !isOwnerView) || isOwnerView;
  const cardTooltip = canClick ? feature.detailsUrl ? t('featuresSection.learnMoreAboutFeatureTooltip', { featureTitle: feature.title }) : t('featuresSection.manageFeatureTooltip', { featureTitle: feature.title }) : feature.title;

  return (
    <motion.div
      animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER}
      className={clsx("bg-white border-2 border-black rounded-xl p-5 flex flex-col justify-between", isOwnerView && feature.isPubliclyVisible === false ? 'opacity-70 border-dashed border-gray-400' : '', canClick ? 'cursor-pointer' : 'cursor-default')}
      onClick={canClick ? handleClick : undefined} title={cardTooltip}
    >
      <div>
        <div className="flex items-start gap-3 mb-2">
          <div className="text-teal-600 mt-0.5">{renderIcon(feature.icon || 'settings', { size: 24 })}</div>
          <div className="flex-1">
            <h3 className="text-md font-semibold text-gray-900 leading-tight">{feature.title}</h3>
            {isOwnerView && feature.isPubliclyVisible === false && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full inline-flex items-center mt-1 border border-slate-300"><EyeOff size={12} className="mr-1" /> {t('featuresSection.hiddenFromPublicBadge')}</span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{feature.description}</p>
        {feature.media && (
          <div className="mb-4 rounded-lg overflow-hidden border-2 border-black aspect-video">
            <VisualElement visual={feature.media} className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      {feature.tags && feature.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-auto pt-3 border-t border-gray-100">
          {feature.tags.map((tag, index) => (
            <Badge
              key={`${feature.id}-tag-${index}`}
              text={tag}
              // --- FIX: Changed "grey" to "gray" to match the allowed types for accentColor.
              accentColor="gray"
              className="text-xs"
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export const FeaturesSection = React.forwardRef<HTMLDivElement, FeaturesSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';

    // --- FIX: Ensured `displayFeatures` is always an array to prevent "possibly 'undefined'" errors.
    const displayFeatures = (isOwnerView
      ? agentData.features
      : agentData.features?.filter(feature => feature.isPubliclyVisible !== false)) || [];

    if (displayFeatures.length === 0) {
      if (isOwnerView) {
        return (
          <Card id={id} ref={ref} titleKey="featuresSection.defaultTitle" icon={<Zap size={22} className="text-sky-600" />} headerBgColor={headerBgColor}
            actionButton={<Button textKey="featuresSection.addFeatureButton" variant="primary" size="sm" icon={<Edit2 size={14} />} onClick={() => onAction('ADD_NEW_FEATURE', null)} />}
          >
            <p className="text-center text-gray-500 py-8">{t('featuresSection.noFeaturesOwnerPrompt')}</p>
          </Card>
        );
      }
      return null;
    }
    
    return (
      <Card id={id} ref={ref} titleKey="featuresSection.defaultTitle" icon={<Zap size={22} className="text-sky-600" />} headerBgColor={headerBgColor}
        actionButton={isOwnerView && agentData.allFeaturesManagementUrl && (
            <Button textKey="featuresSection.manageAllFeaturesButton" variant="outline" size="sm" icon={<Settings size={14} />} onClick={() => onAction('MANAGE_ALL_FEATURES', { url: agentData.allFeaturesManagementUrl })} />
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayFeatures.map((feature) => (
            <FeatureCard key={feature.id} feature={feature} isOwnerView={isOwnerView} onAction={onAction} />
          ))}
        </div>
        {!isOwnerView && agentData.allFeaturesManagementUrl && (
          <div className="mt-8 text-center">
            <Button textKey="featuresSection.exploreAllButton" variant="secondary" icon={<ExternalLink size={16} />} iconPosition="right" onClick={() => onAction('EXPLORE_ALL_FEATURES_PUBLIC', { url: agentData.allFeaturesManagementUrl })} />
          </div>
        )}
      </Card>
    );
  }
);
FeaturesSection.displayName = 'FeaturesSection';